import React, { useContext, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
} from "alm-react-components";
import { t } from "i18next";
import {
  ActionType,
  SET_POPUP_IDEAL_TIMEOUT_DURATION,
} from "../../assets/constants/constants";
import { AppContext } from "../../context/AppProvider";
import { formatTimeRemaining, removeChainlitPopup } from "../../lib/utils";

interface SessionTimeoutPopupProps {
  onExtendSessionTime: () => void;
  onLogout: () => void;
  onIdleTimeout: () => void;
}

const SessionTimeoutPopup: React.FC<SessionTimeoutPopupProps> = ({
  onExtendSessionTime,
  onLogout,
  onIdleTimeout,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const [timeRemaining, setTimeRemaining] = useState<number>(
    SET_POPUP_IDEAL_TIMEOUT_DURATION
  );

  const handleExtendSessionClick = () => {
    // Store the user's choice to extend the session
    onExtendSessionTime();
    dispatch({
      type: ActionType.SESSION_TIMEOUT_POPUP,
      payload: { visible: false, message: "" },
    });
  };

  const handleNoSessionExtended = () => {
    removeChainlitPopup();
    
    // User explicitly chose to log out -
    // Clear lastActiveTimestamp
    sessionStorage.removeItem("lastActiveTimestamp"); //
    sessionStorage.removeItem("popupExpiryTimestamp");
    onLogout();
    dispatch({
      type: ActionType.SESSION_TIMEOUT_POPUP,
      payload: { visible: false, message: "" },
    });
  };

  useEffect(() => {
    // Reset timer when the popup becomes visible
    if (state?.sessionTimeoutPopup?.visible) {
      // Store the expiry time in sessionStorage for cross-tab synchronization
      const existingExpiry = sessionStorage.getItem("popupExpiryTimestamp");

      if (!existingExpiry) {
        const expiryTime = Date.now() + SET_POPUP_IDEAL_TIMEOUT_DURATION;
        sessionStorage.setItem("popupExpiryTimestamp", expiryTime.toString());
        setTimeRemaining(SET_POPUP_IDEAL_TIMEOUT_DURATION);
      } else {
        // If returning to tab, calculate remaining time
        const expiryTime = parseInt(existingExpiry);
        const remaining = Math.max(0, expiryTime - Date.now());
        setTimeRemaining(remaining);
      }

      // Dispatch a storage event to notify other tabs
      window.dispatchEvent(new Event("storage"));
    }
  }, [state?.sessionTimeoutPopup?.visible]);

  useEffect(() => {
    // Set ideal timeout if the popup is visible
    if (state?.sessionTimeoutPopup?.visible) {
      // Get the expiry time from sessionStorage or create a new one
      let expiryTimeStr = sessionStorage.getItem("popupExpiryTimestamp");
      if (!expiryTimeStr) {
        const newExpiryTime = Date.now() + SET_POPUP_IDEAL_TIMEOUT_DURATION;
        expiryTimeStr = newExpiryTime.toString();
        sessionStorage.setItem("popupExpiryTimestamp", expiryTimeStr);
      }

      const expiryTime = parseInt(expiryTimeStr);

      // Calculate time until expiry
      const timeUntilExpiry = Math.max(0, expiryTime - Date.now());

      const timeoutId = setTimeout(() => {
        dispatch({
          type: ActionType.SESSION_TIMEOUT_POPUP,
          payload: { visible: false, message: "" },
        });
        onIdleTimeout();
      }, timeUntilExpiry);

      // Countdown timer interval
      const countdownInterval = setInterval(() => {
        const currentTime = Date.now();
        const remaining = Math.max(0, expiryTime - currentTime);

        setTimeRemaining(remaining);

        if (remaining <= 1000) {
          // Clear the interval when time reaches 0
          clearInterval(countdownInterval);

          // Trigger logout when countdown reaches zero
          dispatch({
            type: ActionType.SESSION_TIMEOUT_POPUP,
            payload: { visible: false, message: "" },
          });
          onIdleTimeout();
        }
      }, 1000);

      // Listen for storage events from other tabs
      const handleStorageChange = () => {
        const popupExpiry = sessionStorage.getItem("popupExpiryTimestamp");

        // If popup expiry was removed (user clicked "Yes" in another tab)
        if (!popupExpiry && state?.sessionTimeoutPopup?.visible) {
          dispatch({
            type: ActionType.SESSION_TIMEOUT_POPUP,
            payload: { visible: false, message: "" },
          });
          clearTimeout(timeoutId);
          clearInterval(countdownInterval);
        } else if (popupExpiry && state?.sessionTimeoutPopup?.visible) {
          // Update the timer if expiry time was changed in another tab
          const expiryTime = parseInt(popupExpiry);
          const remaining = Math.max(0, expiryTime - Date.now());
          setTimeRemaining(remaining);
        }
      };

      window.addEventListener("storage", handleStorageChange);

      // Cleanup on unmount or if the popup is dismissed
      return () => {
        clearTimeout(timeoutId);
        clearInterval(countdownInterval);
        window.removeEventListener("storage", handleStorageChange);
      };
    }
  }, [state?.sessionTimeoutPopup?.visible, onIdleTimeout, dispatch]);

  return (
    <div className="fixed inset-0 bg-black/50">
      <div className=" z-[10000] ">
        <AlertDialog open={state?.sessionTimeoutPopup?.visible}>
          <AlertDialogContent className="bg-[#16171C] p-12 w-full text-center border border-primary overflow-hidden max-sm:w-[94%] max-sm:p-6 z-[10000]">
            <AlertDialogDescription className="flex py-8 pt-0 flex-col text-2xl leading-8 font-barlowRegular text-white items-center h-auto">
              <div className="z-[10000]">
                <div className="flex justify-center">
                  <h3 className="text-2xl font-barlowRegular text-white">
                    {t("Common.sessionTimeoutAlertMessage")}
                  </h3>
                </div>
                <div className="text-2xl font-barlowRegular text-white mt-4 font-bold">
                  {formatTimeRemaining(timeRemaining)}
                </div>
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter className="lg:justify-center z-[1] max-sm:flex-col">
              <AlertDialogAction
                onClick={handleExtendSessionClick}
                className="w-full font-opensansSemiBold text-base capitalize my-2"
              >
                {t("Common.okButton")}
              </AlertDialogAction>
              <AlertDialogAction
                onClick={handleNoSessionExtended}
                className="w-full font-opensansSemiBold text-base capitalize my-2"
              >
                {t("Common.noButton")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SessionTimeoutPopup;